import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Lock, Mail } from "lucide-react";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import SuccessToast from "../components/SuccessToast";
import {
  CONSULTATION_STATUSES,
  consultationIsoFromLocal,
  formatConsultationDateTime,
  getConsultationStatusLabel,
  normalizeConsultationStatus,
  toDatetimeLocalValue,
} from "../lib/consultationSchedule";
import {
  CONTRACT_BUCKET,
  CONTRACT_REVIEW_STATUSES,
  PROPOSAL_REVIEW_STATUSES,
  canAdminAcceptContract,
  canAdminRequestContractResubmission,
  deriveAdminUiStatus,
  getContractReviewLabel,
  getProposalReviewLabel,
  getStoredContract,
  hasSignedContract,
  isBookingEventLive,
  isContractAccepted,
  isProposalContractComplete,
  pickProposalEditValues,
} from "../lib/proposalContract";
import {
  canonicalizeStatus,
  DECLINE_REASON_OPTIONS,
  DECLINE_REASON_OTHER,
  getStageFieldVisibility,
  isCurrentStage,
  isDeclinedStatus,
  resolveDeclineReason,
} from "./bookingStageFields";
import {
  FUTURE_DATE_ERROR,
  getDateInputMinYmd,
  isAllowedFutureOrUnchangedDate,
} from "../lib/dateTime";
import {
  displayMoneyValue,
  DOWN_PAYMENT_EXCEEDS_ERROR,
  formatIntegerCommas,
  normalizeMoneyTyping,
  parseIntegerMoney,
  parseStoredIntegerMoney,
  toStoredIntegerMoney,
  validateDownPayment,
  validatePositiveIntegerMoney,
} from "../lib/money";

const POSTED_LOCK_MESSAGE =
  "Editing is unavailable because this booking has already been posted as a live event.";

// Payload keys that carry booking information. Stage, workflow, and audit columns stay out
// of this list so status changes and the contract workflow keep working while locked.
const LOCKED_INFO_FIELDS = [
  "requester_name",
  "requester_email",
  "requester_phone",
  "company_name",
  "echojham_representative",
  "approved_quotation_total",
  "down_payment_amount",
  "balance_due_date",
  "payment_method",
  "event_name",
  "theme",
  "venue",
  "budget_range",
  "attendee_count",
  "specific_needs",
  "details",
  "concert_vendor_choices",
  "consultation_scheduled_at",
  "consultation_status",
  "meeting_place",
  "meeting_instructions",
];

const BOOKING_UPDATE_KEYS = [
  ...LOCKED_INFO_FIELDS,
  "status",
  "project_stage",
  "updated_at",
  "decline_reason",
  "declined_at",
];

const isDomEventLike = (value) => Boolean(
  value
  && typeof value === "object"
  && (value.nativeEvent || value instanceof Event || value.target || value.currentTarget),
);

const pickBookingUpdatePayload = (source) => {
  const payload = {};
  BOOKING_UPDATE_KEYS.forEach((key) => {
    if (source && Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined) {
      payload[key] = source[key];
    }
  });
  return payload;
};

const statusLabel = (value) => {
  if (value === "in_review") return "In Review";
  if (value === "initial_consultation") return "Initial Consultation";
  if (value === "proposal_contract" || value === "proposal_quotation" || value === "agreement_confirmation" || value === "contract_signing") {
    return "Proposal & Contract";
  }
  if (value === "event_preparation") return "Event Preparation";
  if (value === "event_live") return "Posted on Live Event";
  if (value === "accepted" || value === "approved") return "Event Preparation";
  if (value === "declined") return "Declined";
  return String(value || "new").charAt(0).toUpperCase() + String(value || "new").slice(1);
};

const summaryValue = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const deriveUiStatus = (record) => deriveAdminUiStatus(record);

const toDbStatus = (uiStatus) => {
  const mapped = canonicalizeStatus(uiStatus);
  if (mapped === "accepted" || mapped === "approved") return "event_preparation";
  return mapped;
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", year: "numeric" });
};

const statusTextClass = (status) => {
  if (status === "accepted" || status === "approved") return "text-green-700";
  if (status === "declined") return "text-red-700";
  if (status === "in_review") return "text-amber-600";
  if (status === "initial_consultation") return "text-blue-600";
  if (status === "proposal_contract" || status === "proposal_quotation" || status === "agreement_confirmation" || status === "contract_signing") return "text-purple-600";
  if (status === "event_preparation") return "text-fuchsia-700";
  if (status === "event_live") return "text-green-700";
  return "text-gray-700";
};

const parseBudgetRange = (budgetRange) => {
  const source = String(budgetRange || "");
  const matches = source.match(/[\d,]+(?:\.\d+)?/g) || [];
  const min = Number(String(matches[0] || "").replace(/,/g, ""));
  const max = Number(String(matches[1] || "").replace(/,/g, ""));

  return {
    min: Number.isFinite(min) ? Math.round(min) : "",
    max: Number.isFinite(max) ? Math.round(max) : "",
  };
};

const formatPesoRange = (min, max) => {
  const minFormatted = formatIntegerCommas(min);
  const maxFormatted = formatIntegerCommas(max);

  if (!minFormatted || !maxFormatted) return "";
  return `₱${minFormatted} – ₱${maxFormatted}`;
};

const storedMoneyValue = (value) => {
  const amount = parseStoredIntegerMoney(value);
  return amount ? String(amount) : String(value || "");
};

const toDateInputValue = (value) => {
  const text = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return "";
};

const mapCommercialFields = (source = {}) => ({
  requester_phone: source.requester_phone || "",
  company_name: source.company_name || "",
  echojham_representative: source.echojham_representative || "",
  approved_quotation_total: storedMoneyValue(source.approved_quotation_total),
  down_payment_amount: storedMoneyValue(source.down_payment_amount),
  balance_due_date: toDateInputValue(source.balance_due_date),
  payment_method: source.payment_method || "",
});

export default function BookingDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [booking, setBooking] = useState(null);
  const [formData, setFormData] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitReason, setResubmitReason] = useState("");
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReasonChoice, setDeclineReasonChoice] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const statusBeforeDeclineRef = useRef(null);
  const [focusedMoneyField, setFocusedMoneyField] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLocked, setIsLocked] = useState(true);
  const editSnapshotRef = useRef(null);

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);

      let { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Failed to load booking details:", error);
        setBooking(null);
        setLoading(false);
        return;
      }

      const normalized = {
        ...data,
        status: deriveUiStatus(data),
      };

      if (normalized.status === "in_review") {
        const createdMs = new Date(normalized.created_at).getTime();
        if (Number.isFinite(createdMs) && Date.now() - createdMs >= 12 * 60 * 60 * 1000) {
          const { error: updateError } = await supabase
            .from("bookings")
            .update({ status: "in_review", updated_at: new Date().toISOString() })
            .eq("id", id)
            .eq("status", "new");

          if (!updateError) {
            normalized.status = "in_review";
          }
        }
      }

      setBooking(normalized);
      const budget = parseBudgetRange(normalized.budget_range);
      setFormData({
        requester_name: normalized.requester_name || "",
        requester_email: normalized.requester_email || "",
        event_name: normalized.event_name || "",
        theme: normalized.theme || "",
        venue: normalized.venue || "",
        budget_min: storedMoneyValue(budget.min),
        budget_max: storedMoneyValue(budget.max),
        attendee_count: normalized.attendee_count || "",
        specific_needs: normalized.specific_needs || "",
        details: normalized.details || "",
        technical_rider: normalized.concert_vendor_choices?.technical_rider || "Let EchoJham Provide",
        musical_director: normalized.concert_vendor_choices?.musical_director || "Let EchoJham Provide",
        food: normalized.concert_vendor_choices?.food || "Let EchoJham Provide",
        status: normalized.status || "in_review",
        consultation_datetime: toDatetimeLocalValue(normalized.consultation_scheduled_at),
        meeting_place: normalized.meeting_place || "",
        meeting_instructions: normalized.meeting_instructions || "",
        ...mapCommercialFields(normalized),
      });
      setLoading(false);
    };

    fetchBooking();
  }, [id]);

  const refreshBookingStatus = async () => {
    let { data, error } = await supabase
      .from("bookings")
      .select("*")
      .eq("id", id)
      .single();

    console.log("🟠 [FETCHED FROM DB AFTER UPDATE]", { data, derivedStatus: deriveUiStatus(data) });

    if (!error && data) {
      const normalized = {
        ...data,
        status: deriveUiStatus(data),
      };
      console.log("🔴 [SETTING STATE AFTER REFRESH]", { normalized });
      setBooking(normalized);
      const budget = parseBudgetRange(normalized.budget_range);
      setFormData((prev) => ({
        ...prev,
        requester_name: normalized.requester_name || "",
        requester_email: normalized.requester_email || "",
        event_name: normalized.event_name || "",
        theme: normalized.theme || "",
        venue: normalized.venue || "",
        budget_min: storedMoneyValue(budget.min),
        budget_max: storedMoneyValue(budget.max),
        attendee_count: normalized.attendee_count || "",
        specific_needs: normalized.specific_needs || "",
        details: normalized.details || "",
        status: normalized.status || "in_review",
        technical_rider: normalized.concert_vendor_choices?.technical_rider || "Let EchoJham Provide",
        musical_director: normalized.concert_vendor_choices?.musical_director || "Let EchoJham Provide",
        food: normalized.concert_vendor_choices?.food || "Let EchoJham Provide",
        consultation_datetime: toDatetimeLocalValue(normalized.consultation_scheduled_at),
        meeting_place: normalized.meeting_place || "",
        meeting_instructions: normalized.meeting_instructions || "",
        ...mapCommercialFields(normalized),
      }));
    }
  };

  const detailBookingType = booking?.booking_type === "corporate" ? "corporate" : "concert";
  const detailsTitle = detailBookingType === "corporate" ? "Corporate Booking Details" : "Concert Booking Details";
  const consultationStatus = normalizeConsultationStatus(booking?.consultation_status);
  const {
    editClientEvent,
    showClientEventSummary,
    editConsultation,
    showConsultationSummary: showConsultationSummaryForStage,
    editProposalContract,
    showProposalContractSummary: showProposalContractSummaryForStage,
    canEditStageFields,
  } = getStageFieldVisibility(formData?.status || booking?.status);
  const signedContractAttachment = getStoredContract(booking);
  const pacComplete = isProposalContractComplete(booking);
  const pendingProposalChanges = booking?.proposal_pending_changes && typeof booking.proposal_pending_changes === "object"
    ? booking.proposal_pending_changes
    : null;
  const hasConsultationData = Boolean(
    booking?.consultation_scheduled_at
    || booking?.meeting_place
    || booking?.meeting_instructions
    || formData?.consultation_datetime
    || formData?.meeting_place
    || formData?.meeting_instructions
  );
  const hasProposalContractData = Boolean(
    formData?.echojham_representative
    || formData?.approved_quotation_total
    || formData?.down_payment_amount
    || formData?.balance_due_date
    || formData?.payment_method
    || booking?.proposal_review_status
    || booking?.client_agreement_confirmed
    || booking?.agreement_confirmed
    || hasSignedContract(booking)
    || pacComplete
  );
  const showConsultationSummary = showConsultationSummaryForStage && hasConsultationData;
  const showProposalContractSummary = showProposalContractSummaryForStage && hasProposalContractData;
  const vendorChoices = useMemo(() => ({
    technical_rider: formData?.technical_rider || "Let EchoJham Provide",
    musical_director: formData?.musical_director || "Let EchoJham Provide",
    food: formData?.food || "Let EchoJham Provide",
  }), [formData]);
  const postedOnLiveEvent = isBookingEventLive(booking);
  const fieldsDisabled = isLocked || postedOnLiveEvent;
  const savedStatus = canonicalizeStatus(booking?.status);
  const isDeclined = isDeclinedStatus(savedStatus);
  const canDecline = !postedOnLiveEvent && !isDeclined && savedStatus !== "event_live";
  const selectedDeclineReason = resolveDeclineReason(declineReasonChoice, declineReason);

  const startEditing = () => {
    if (postedOnLiveEvent || isDeclined || !formData) return;
    editSnapshotRef.current = { ...formData };
    setIsLocked(false);
  };

  const cancelEditing = () => {
    if (editSnapshotRef.current) setFormData(editSnapshotRef.current);
    editSnapshotRef.current = null;
    setFieldErrors({});
    setIsLocked(true);
  };

  const exportBookingDetails = () => {
    if (!booking) return;

    const lines = [
      `Booking Code: ${booking.request_code || "-"}`,
      `Booking Type: ${booking.booking_type || "-"}`,
      `Client Name: ${formData?.requester_name || booking.requester_name || "-"}`,
      `Email: ${formData?.requester_email || booking.requester_email || "-"}`,
      `Phone: ${formData?.requester_phone || booking.requester_phone || "-"}`,
      `Company: ${formData?.company_name || booking.company_name || "-"}`,
      `Event Name: ${formData?.event_name || booking.event_name || "-"}`,
      `Status: ${statusLabel(formData?.status || booking.status)}`,
      `Theme: ${formData?.theme || booking.theme || "-"}`,
      `Venue: ${formData?.venue || booking.venue || "-"}`,
      `Budget: ${formatPesoRange(formData?.budget_min, formData?.budget_max) || booking.budget_range || "-"}`,
      `Target Date: ${formatDisplayDate(booking.event_date || booking.created_at)}`,
      `Attendees: ${formData?.attendee_count || booking.attendee_count || "-"}`,
      "",
      "Specific Requirements:",
      formData?.specific_needs || booking.specific_needs || "-",
      "",
      "Details:",
      formData?.details || booking.details || "-",
      "",
      "Decline Reason:",
      booking.decline_reason || formData?.decline_reason || "-",
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${booking.request_code || `booking-${booking.id}`}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const prepareUpdatePayload = (snapshot = formData, { includeInfoFields = true } = {}) => {
    const nextStatus = canonicalizeStatus(snapshot?.status || booking.status);
    const nextDbStatus = toDbStatus(nextStatus);
    const nextBudgetRange = formatPesoRange(snapshot?.budget_min, snapshot?.budget_max) || booking.budget_range || "";

    const updatePayload = {
      status: nextDbStatus,
      project_stage: nextDbStatus,
      requester_name: String(snapshot?.requester_name || "").trim(),
      requester_email: String(snapshot?.requester_email || "").trim(),
      requester_phone: String(snapshot?.requester_phone || "").trim() || null,
      company_name: String(snapshot?.company_name || "").trim() || null,
      echojham_representative: String(snapshot?.echojham_representative || "").trim() || null,
      approved_quotation_total: toStoredIntegerMoney(snapshot?.approved_quotation_total) || null,
      down_payment_amount: toStoredIntegerMoney(snapshot?.down_payment_amount) || null,
      balance_due_date: toDateInputValue(snapshot?.balance_due_date) || null,
      payment_method: String(snapshot?.payment_method || "").trim() || null,
      event_name: String(snapshot?.event_name || "").trim(),
      theme: String(snapshot?.theme || "").trim(),
      venue: String(snapshot?.venue || "").trim(),
      budget_range: nextBudgetRange,
      attendee_count: snapshot?.attendee_count ? Number(snapshot.attendee_count) : null,
      specific_needs: String(snapshot?.specific_needs || "").trim(),
      details: String(snapshot?.details || "").trim(),
      concert_vendor_choices: detailBookingType === "concert" ? vendorChoices : booking.concert_vendor_choices || {},
      updated_at: new Date().toISOString(),
    };

    const consultationIso = consultationIsoFromLocal(snapshot?.consultation_datetime);
    if (consultationIso) {
      updatePayload.consultation_scheduled_at = consultationIso;
      updatePayload.consultation_status = CONSULTATION_STATUSES.SCHEDULED;
    } else if (booking?.consultation_scheduled_at) {
      updatePayload.consultation_scheduled_at = booking.consultation_scheduled_at;
      updatePayload.consultation_status = booking.consultation_status || CONSULTATION_STATUSES.SUBMITTED;
    }

    const meetingPlace = String(snapshot?.meeting_place || "").trim();
    const meetingInstructions = String(snapshot?.meeting_instructions || "").trim();
    updatePayload.meeting_place = meetingPlace || null;
    updatePayload.meeting_instructions = meetingInstructions || null;

    if (nextDbStatus === "declined") {
      const reason = String(snapshot?.decline_reason || declineReason || booking?.decline_reason || "").trim();
      updatePayload.decline_reason = reason || null;
      updatePayload.declined_at = booking?.declined_at || new Date().toISOString();
    }

    if (!includeInfoFields) {
      LOCKED_INFO_FIELDS.forEach((field) => {
        delete updatePayload[field];
      });
    }

    console.log("🔵 [PAYLOAD PREPARED]", {
      formDataStatus: formData?.status,
      nextStatus, 
      nextDbStatus, 
      project_stage: updatePayload.project_stage,
      updatePayload 
    });
    return updatePayload;
  };

  const validateCommercialFields = (snapshot = formData) => {
    const nextErrors = {};
    const snapshotStatus = canonicalizeStatus(snapshot?.status || booking?.status);
    const validateClientEvent = isCurrentStage(snapshotStatus, "in_review") || isCurrentStage(snapshotStatus, "proposal_contract");
    const validateContract = isCurrentStage(snapshotStatus, "proposal_contract");

    if (validateClientEvent) {
      const requesterName = String(snapshot?.requester_name ?? "").trim();
      if (!requesterName) {
        nextErrors.requester_name = "Client name is required.";
      }

      const requesterEmail = String(snapshot?.requester_email ?? "").trim();
      if (!requesterEmail) {
        nextErrors.requester_email = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(requesterEmail)) {
        nextErrors.requester_email = "Enter a valid email address.";
      }

      const budgetMinText = String(snapshot?.budget_min ?? "").trim();
      const budgetMaxText = String(snapshot?.budget_max ?? "").trim();

      if (budgetMinText && parseIntegerMoney(budgetMinText) == null) {
        nextErrors.budget_min = "Enter a positive whole number.";
      }
      if (budgetMaxText && parseIntegerMoney(budgetMaxText) == null) {
        nextErrors.budget_max = "Enter a positive whole number.";
      }
    }

    if (validateContract) {
      const quotationText = String(snapshot?.approved_quotation_total ?? "").trim();
      const downPaymentText = String(snapshot?.down_payment_amount ?? "").trim();
      if (quotationText) {
        const quotationError = validatePositiveIntegerMoney(quotationText);
        if (quotationError) nextErrors.approved_quotation_total = quotationError;
      }
      if (downPaymentText) {
        const downPaymentError = validatePositiveIntegerMoney(downPaymentText);
        if (downPaymentError) nextErrors.down_payment_amount = downPaymentError;
      }
      if (!nextErrors.approved_quotation_total && !nextErrors.down_payment_amount && quotationText && downPaymentText) {
        const comparisonError = validateDownPayment(quotationText, downPaymentText);
        if (comparisonError) nextErrors.down_payment_amount = comparisonError;
      }

      if (!isAllowedFutureOrUnchangedDate(snapshot?.balance_due_date, { originalYmd: booking?.balance_due_date })) {
        nextErrors.balance_due_date = FUTURE_DATE_ERROR;
      }
    }

    return nextErrors;
  };

  const openDeclineModal = (fromStatus = formData?.status) => {
    if (!canDecline) {
      setToastMessage("Posted live events cannot be declined.");
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    statusBeforeDeclineRef.current = canonicalizeStatus(fromStatus);
    setDeclineReasonChoice("");
    setDeclineReason("");
    setFormData((prev) => ({ ...prev, status: "declined" }));
    setShowDeclineModal(true);
  };

  const cancelDecline = () => {
    const previousStatus = statusBeforeDeclineRef.current || savedStatus;
    setShowDeclineModal(false);
    setDeclineReasonChoice("");
    setDeclineReason("");
    setFormData((prev) => ({ ...prev, status: previousStatus }));
  };

  const confirmDecline = () => {
    const reason = resolveDeclineReason(declineReasonChoice, declineReason);
    if (!reason) {
      setToastMessage("A reason is required to decline this booking.");
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    const snapshot = { ...formData, status: "declined", decline_reason: reason };
    const payload = prepareUpdatePayload(snapshot, { includeInfoFields: !fieldsDisabled });
    payload.decline_reason = reason;
    payload.declined_at = new Date().toISOString();
    setShowDeclineModal(false);
    setConfirmModalData({ ...payload, uiStatus: "declined" });
    updateBookingDetails({ ...payload, uiStatus: "declined" });
  };

  const showConfirmation = () => {
    if (!booking || statusUpdating || isDeclined) return;
    const nextStatus = canonicalizeStatus(formData?.status || booking.status);
    if (nextStatus === "declined") {
      openDeclineModal(savedStatus);
      return;
    }
    const nextErrors = validateCommercialFields();
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setToastMessage(Object.values(nextErrors)[0]);
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    setFieldErrors({});
    const payload = prepareUpdatePayload(formData, { includeInfoFields: !fieldsDisabled });
    setConfirmModalData({ ...payload, uiStatus: nextStatus });
    setShowConfirmModal(true);
  };

  const updateMoneyField = (name, value) => {
    const nextValue = normalizeMoneyTyping(value);
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setFieldErrors((prev) => {
      const next = { ...prev, [name]: validatePositiveIntegerMoney(nextValue) };
      if (name === "approved_quotation_total" || name === "down_payment_amount") {
        const quotation = name === "approved_quotation_total" ? nextValue : formData.approved_quotation_total;
        const downPayment = name === "down_payment_amount" ? nextValue : formData.down_payment_amount;
        if (validateDownPayment(quotation, downPayment) === DOWN_PAYMENT_EXCEEDS_ERROR) {
          next.down_payment_amount = DOWN_PAYMENT_EXCEEDS_ERROR;
        } else if (prev.down_payment_amount === DOWN_PAYMENT_EXCEEDS_ERROR) {
          next.down_payment_amount = validatePositiveIntegerMoney(downPayment);
        }
      }
      return next;
    });
  };

  const acceptBookingAfterContract = () => {
    if (!booking || statusUpdating || !pacComplete) return;

    const nextFormSnapshot = {
      ...formData,
      status: "event_preparation",
    };
    const nextErrors = validateCommercialFields(formData);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setToastMessage(Object.values(nextErrors)[0]);
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    const payload = prepareUpdatePayload(nextFormSnapshot, { includeInfoFields: !fieldsDisabled });
    setFormData((prev) => ({
      ...prev,
      status: "event_preparation",
    }));
    setConfirmModalData({ ...payload, uiStatus: "event_preparation" });
    setShowConfirmModal(true);
  };

  const resolveContractUrl = (attachment) => {
    if (!attachment) return "";
    if (attachment.url) return attachment.url;
    if (!attachment.path) return "";
    return supabase.storage.from(CONTRACT_BUCKET).getPublicUrl(attachment.path).data?.publicUrl || "";
  };

  const reviewProposalChanges = async (decision) => {
    if (!booking || statusUpdating) return;
    setStatusUpdating(true);
    const pending = pickProposalEditValues(pendingProposalChanges || booking);
    const payload = {
      status: "proposal_contract",
      project_stage: "proposal_contract",
      proposal_review_status: decision === "approve" ? PROPOSAL_REVIEW_STATUSES.APPROVED : PROPOSAL_REVIEW_STATUSES.PENDING_CLIENT,
      updated_at: new Date().toISOString(),
    };
    if (decision === "approve") {
      payload.event_name = pending.event_name;
      payload.theme = pending.theme;
      payload.venue = pending.venue;
      payload.budget_range = pending.budget_range || booking.budget_range || "";
      payload.attendee_count = pending.attendee_count;
      payload.event_type = pending.event_type;
      payload.details = pending.details;
      payload.specific_needs = pending.specific_needs;
      payload.concert_vendor_choices = pending.concert_vendor_choices || booking.concert_vendor_choices || {};
      payload.proposal_pending_changes = null;
    }

    const { error } = await supabase.from("bookings").update(payload).eq("id", booking.id).select("*");
    if (error) {
      setToastMessage(error.message || "Failed to update proposal review.");
      setStatusUpdating(false);
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    setToastMessage(decision === "approve" ? "Proposal details approved." : "Revision requested from the client.");
    setStatusUpdating(false);
    setTimeout(() => setToastMessage(null), 3000);
    await refreshBookingStatus();
  };

  const acceptSignedContract = async () => {
    if (!booking || statusUpdating || !canAdminAcceptContract(booking)) return;
    setStatusUpdating(true);
    const { error } = await supabase.from("bookings").update({
      contract_review_status: CONTRACT_REVIEW_STATUSES.ACCEPTED,
      contract_revision_message: null,
      contract_accepted_at: new Date().toISOString(),
      status: "proposal_contract",
      project_stage: "proposal_contract",
      updated_at: new Date().toISOString(),
    }).eq("id", booking.id).select("*");
    if (error) {
      setToastMessage(error.message || "Failed to accept the signed contract.");
      setStatusUpdating(false);
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    setToastMessage("Contract Accepted.");
    setStatusUpdating(false);
    setTimeout(() => setToastMessage(null), 3000);
    await refreshBookingStatus();
  };

  const requestContractResubmission = async () => {
    const reason = String(resubmitReason || "").trim();
    if (!booking || statusUpdating) return;
    if (!canAdminRequestContractResubmission(booking, reason)) {
      setToastMessage("A reason is required to request contract resubmission.");
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    setStatusUpdating(true);
    const { error } = await supabase.from("bookings").update({
      contract_review_status: CONTRACT_REVIEW_STATUSES.NEEDS_REVISION,
      contract_revision_message: reason,
      status: "proposal_contract",
      project_stage: "proposal_contract",
      updated_at: new Date().toISOString(),
    }).eq("id", booking.id).select("*");
    if (error) {
      setToastMessage(error.message || "Failed to request contract resubmission.");
      setStatusUpdating(false);
      setTimeout(() => setToastMessage(null), 4000);
      return;
    }
    setShowResubmitModal(false);
    setResubmitReason("");
    setToastMessage("Contract resubmission requested.");
    setStatusUpdating(false);
    setTimeout(() => setToastMessage(null), 3000);
    await refreshBookingStatus();
  };

  const updateBookingDetails = async (modalData = confirmModalData) => {
    const source = isDomEventLike(modalData) ? confirmModalData : modalData;
    if (!booking || statusUpdating || !source) return;

    setStatusUpdating(true);
    setShowConfirmModal(false);

    const uiStatus = source.uiStatus;
    const updatePayload = pickBookingUpdatePayload(source);
    const nextStage = toDbStatus(uiStatus);
    const previousStage = deriveUiStatus(booking);

    if (nextStage === "declined" && (previousStage === "event_live" || isBookingEventLive(booking))) {
      setToastMessage("Posted live events cannot be declined.");
      setStatusUpdating(false);
      setTimeout(() => setToastMessage(null), 5000);
      return;
    }

    // The booking may have gone live in another tab while this form was open.
    const touchesInfoFields = LOCKED_INFO_FIELDS.some((field) => field in updatePayload);
    if (touchesInfoFields) {
      const { data: fresh, error: freshError } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", booking.id)
        .single();

      if (freshError) {
        setToastMessage(freshError.message || "Failed to verify the booking before saving.");
        setStatusUpdating(false);
        setTimeout(() => setToastMessage(null), 5000);
        return;
      }

      if (isBookingEventLive(fresh)) {
        setToastMessage(`Failed to save. ${POSTED_LOCK_MESSAGE}`);
        setStatusUpdating(false);
        setTimeout(() => setToastMessage(null), 6000);
        await refreshBookingStatus();
        return;
      }
    }

    if (nextStage !== previousStage) {
      const { data: rpcData, error: rpcError } = await supabase.rpc("admin_advance_booking_stage", {
        p_booking_id: String(booking.id),
        p_stage: nextStage,
      });
      if (rpcError || rpcData?.ok === false) {
        const message = rpcData?.message || rpcError?.message || "Unable to advance booking stage.";
        setToastMessage(message);
        setStatusUpdating(false);
        setTimeout(() => setToastMessage(null), 5000);
        return;
      }
      updatePayload.status = rpcData?.stage || nextStage;
      updatePayload.project_stage = rpcData?.stage || nextStage;
    }

    let { error } = await supabase
      .from("bookings")
      .update(updatePayload)
      .eq("id", booking.id)
      .select("*");

    const isMeetingColumnMissing =
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("meeting_place") ||
        String(error.message || "").toLowerCase().includes("meeting_instructions"));

    if (isMeetingColumnMissing) {
      const fallbackPayload = { ...updatePayload };
      delete fallbackPayload.meeting_place;
      delete fallbackPayload.meeting_instructions;
      const fallbackResult = await supabase
        .from("bookings")
        .update(fallbackPayload)
        .eq("id", booking.id)
        .select("*");
      error = fallbackResult.error;
    }

    const isDeclineColumnMissing =
      error &&
      (String(error.code || "") === "42703" ||
        String(error.message || "").toLowerCase().includes("decline_reason") ||
        String(error.message || "").toLowerCase().includes("declined_at"));

    if (isDeclineColumnMissing) {
      const fallbackPayload = { ...updatePayload };
      delete fallbackPayload.decline_reason;
      delete fallbackPayload.declined_at;
      const fallbackResult = await supabase
        .from("bookings")
        .update(fallbackPayload)
        .eq("id", booking.id)
        .select("*");
      error = fallbackResult.error;
    }

    if (error) {
      console.error("Failed to update booking details:", error);
      setToastMessage(error.message || "Failed to update booking details.");
      setStatusUpdating(false);
      setTimeout(() => setToastMessage(null), 5000);
      return;
    }

    const newUiStatus = toDbStatus(uiStatus);
    setConfirmModalData(null);
    
    setBooking((prev) => ({
      ...prev,
      ...updatePayload,
      status: newUiStatus,
    }));
    
    setFormData((prev) => ({
      ...prev,
      status: newUiStatus,
    }));
    
    editSnapshotRef.current = null;
    setIsLocked(true);
    setToastMessage("Booking details updated successfully.");
    setStatusUpdating(false);
    setTimeout(() => setToastMessage(null), 3000);
    
    await refreshBookingStatus();
  };

  const sharedButtonClass = "inline-flex h-11 min-w-[138px] w-full sm:w-auto items-center justify-center gap-1.5 rounded-lg px-3.5 text-xs font-semibold transition disabled:opacity-50";
  const disabledFieldClass = "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";
  const inputClass = `h-9 w-full rounded border border-gray-300 bg-gray-50 px-3 text-xs text-gray-800 focus:border-gray-500 focus:bg-white focus:outline-none ${disabledFieldClass}`;
  const textAreaClass = `w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-800 focus:border-gray-500 focus:bg-white focus:outline-none ${disabledFieldClass}`;
  const statusSelectColorClass = (status) => {
    if (status === "accepted" || status === "approved") return "border-green-500 bg-green-50 text-green-700 font-semibold";
    if (status === "declined") return "border-red-500 bg-red-50 text-red-700 font-semibold";
    if (status === "initial_consultation") return "border-blue-500 bg-blue-50 text-blue-700 font-semibold";
    if (status === "proposal_contract") return "border-purple-500 bg-purple-50 text-purple-700 font-semibold";
    if (status === "event_preparation") return "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700 font-semibold";
    if (status === "event_live") return "border-green-500 bg-green-50 text-green-700 font-semibold";
    return "border-amber-400 bg-amber-50 text-amber-700 font-semibold";
  };

  return (
    <div className="min-h-screen bg-[#f3f3f3]">
      <AdminTopBar />
      <SuccessToast message={toastMessage} variant={toastMessage?.includes("Failed") ? "error" : "success"} />

      <div className="px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8">
        <div className="w-full min-w-0">
          {loading ? (
            <div className="w-full rounded-xl border border-gray-300 bg-white p-8 text-sm text-gray-500">Loading booking details...</div>
          ) : !booking || !formData ? (
            <div className="w-full rounded-xl border border-gray-300 bg-white p-8 text-sm text-gray-500">Booking not found.</div>
          ) : (
            <div className="w-full min-w-0">
              <div className="border-b border-gray-300 px-4 py-3 md:px-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/bookings")}
                    className="inline-flex items-center gap-2 text-gray-700 hover:text-black min-w-0"
                  >
                    <ArrowLeft size={16} className="shrink-0" />
                    <span className="text-2xl md:text-[26px] font-black tracking-tight italic text-gray-900 break-words">{detailsTitle}</span>
                  </button>

                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {postedOnLiveEvent ? (
                      <p className="flex w-full sm:max-w-[420px] items-center gap-2 text-xs font-semibold text-gray-500">
                        <Lock size={14} className="shrink-0" /> {POSTED_LOCK_MESSAGE}
                      </p>
                    ) : isLocked && canEditStageFields && !isDeclined ? (
                      <button
                        type="button"
                        onClick={startEditing}
                        className={`${sharedButtonClass} border border-gray-500 bg-white text-gray-800 hover:bg-gray-100`}
                      >
                        <Lock size={13} /> Edit Info
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={exportBookingDetails}
                      className={`${sharedButtonClass} bg-[#8b0000] text-white hover:bg-[#6f0000]`}
                    >
                      <FileDown size={13} /> Export File
                    </button>
                    <a
                      href={`mailto:${booking.requester_email || ""}`}
                      className={`${sharedButtonClass} border border-gray-500 bg-white text-gray-800 hover:bg-gray-100`}
                    >
                      <Mail size={13} /> Email Client
                    </a>
                  </div>
                </div>

                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 px-0 sm:px-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Event Name</p>
                    <div className="mt-1 inline-flex max-w-full rounded-full bg-[#8b0000] px-4 py-2 text-sm font-semibold text-white shadow-sm">
                      <span className="break-words">{formData.event_name || booking.event_name || "Untitled Event"}</span>
                    </div>
                  </div>

                  <label className="text-xs font-semibold text-gray-700 sm:text-right shrink-0 w-full sm:w-auto">
                    Booking Status
                    <select
                      value={formData.status}
                      disabled={isDeclined}
                      onChange={(event) => {
                        const nextStatus = canonicalizeStatus(event.target.value);
                        if (nextStatus === "declined") {
                          openDeclineModal(formData.status);
                          return;
                        }
                        setFormData((prev) => ({ ...prev, status: nextStatus }));
                      }}
                      className={`${inputClass} mt-1 min-w-0 sm:min-w-[190px] ${statusSelectColorClass(formData.status)}`}
                    >
                      <option value="in_review" className="bg-amber-50 text-amber-700">In Review</option>
                      <option value="initial_consultation" className="bg-blue-50 text-blue-700">Initial Consultation</option>
                      <option value="proposal_contract" className="bg-purple-50 text-purple-700">Proposal & Contract</option>
                      <option value="event_preparation" className="bg-fuchsia-50 text-fuchsia-700">Event Preparation</option>
                      <option value="event_live" className="bg-green-50 text-green-700">Posted on Live Event</option>
                      {canDecline || isDeclined ? (
                        <option value="declined" className="bg-red-50 text-red-700">Declined</option>
                      ) : null}
                    </select>
                    <p className={`mt-1 text-[11px] ${statusTextClass(formData.status)}`}>{statusLabel(formData.status)}</p>
                  </label>
                </div>
              </div>

              <div className="space-y-4 px-4 py-4 md:px-6 md:py-5">
                {isDeclined ? (
                  <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-950">
                    <p className="font-semibold">This booking was declined.</p>
                    <p className="mt-1 text-xs">{booking.decline_reason || formData.decline_reason || "No reason recorded."}</p>
                  </div>
                ) : null}

                {showClientEventSummary ? (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="bg-black px-4 py-2 text-sm font-bold text-white">Client and Event Information</div>
                    <div className="grid gap-3 p-4 text-xs text-gray-700 md:grid-cols-2 md:p-6">
                      <p><span className="font-semibold">Client Name:</span> {summaryValue(formData.requester_name)}</p>
                      <p><span className="font-semibold">Email:</span> {summaryValue(formData.requester_email)}</p>
                      <p><span className="font-semibold">Phone:</span> {summaryValue(formData.requester_phone)}</p>
                      {detailBookingType === "corporate" ? (
                        <p><span className="font-semibold">Company Name:</span> {summaryValue(formData.company_name)}</p>
                      ) : null}
                      <p><span className="font-semibold">Theme:</span> {summaryValue(formData.theme)}</p>
                      <p><span className="font-semibold">Venue:</span> {summaryValue(formData.venue)}</p>
                      <p><span className="font-semibold">Budget:</span> {formatPesoRange(formData.budget_min, formData.budget_max) || booking.budget_range || "—"}</p>
                      {detailBookingType === "corporate" ? (
                        <p><span className="font-semibold">Number of Attendees:</span> {summaryValue(formData.attendee_count)}</p>
                      ) : (
                        <p><span className="font-semibold">Event Name:</span> {summaryValue(formData.event_name)}</p>
                      )}
                      {detailBookingType === "corporate" ? (
                        <p><span className="font-semibold">Target Date:</span> {formatDisplayDate(booking.event_date || booking.created_at)}</p>
                      ) : null}
                      {detailBookingType === "concert" ? (
                        <>
                          <p><span className="font-semibold">Technical Rider:</span> {summaryValue(formData.technical_rider)}</p>
                          <p><span className="font-semibold">Musical Director:</span> {summaryValue(formData.musical_director)}</p>
                          <p><span className="font-semibold">Food:</span> {summaryValue(formData.food)}</p>
                          <p className="md:col-span-2"><span className="font-semibold">Additional Requirements:</span> {summaryValue(formData.details)}</p>
                        </>
                      ) : (
                        <>
                          <p className="md:col-span-2"><span className="font-semibold">Specific Requirements:</span> {summaryValue(formData.specific_needs)}</p>
                          <p className="md:col-span-2"><span className="font-semibold">Event Details & Other Requirements:</span> {summaryValue(formData.details)}</p>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}

                {editClientEvent ? (
                <>
                <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                  <div className="bg-black px-4 py-2 text-sm font-bold text-white">Client and Event Information</div>
                  <div className="grid gap-6 p-4 md:grid-cols-[1.2fr_0.8fr] md:p-6">
                    <div className="grid grid-cols-1 gap-3">
                      <label className="text-xs font-semibold text-gray-700">
                        Client Name
                        <input
                          value={formData.requester_name}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, requester_name: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                        {fieldErrors.requester_name ? (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.requester_name}</p>
                        ) : null}
                      </label>

                      <label className="text-xs font-semibold text-gray-700">
                        Email
                        <input
                          value={formData.requester_email}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, requester_email: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                        {fieldErrors.requester_email ? (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.requester_email}</p>
                        ) : null}
                      </label>

                      <label className="text-xs font-semibold text-gray-700">
                        Phone
                        <input
                          value={formData.requester_phone}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, requester_phone: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                      </label>

                      {detailBookingType === "corporate" ? (
                        <label className="text-xs font-semibold text-gray-700">
                          Company Name
                          <input
                            value={formData.company_name}
                            disabled={fieldsDisabled}
                            onChange={(event) => setFormData((prev) => ({ ...prev, company_name: event.target.value }))}
                            className={`${inputClass} mt-1`}
                          />
                        </label>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:pt-1">
                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Theme
                        <input
                          value={formData.theme}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, theme: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                      </label>

                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Venue
                        <input
                          value={formData.venue}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, venue: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                      </label>

                      <label className="text-xs font-semibold text-gray-700 md:text-right">
                        Budget
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={displayMoneyValue(formData.budget_min, { focused: focusedMoneyField === "budget_min" })}
                            disabled={fieldsDisabled}
                            onFocus={() => setFocusedMoneyField("budget_min")}
                            onBlur={() => setFocusedMoneyField("")}
                            onChange={(event) => updateMoneyField("budget_min", event.target.value)}
                            className={inputClass}
                            placeholder="Min"
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={displayMoneyValue(formData.budget_max, { focused: focusedMoneyField === "budget_max" })}
                            disabled={fieldsDisabled}
                            onFocus={() => setFocusedMoneyField("budget_max")}
                            onBlur={() => setFocusedMoneyField("")}
                            onChange={(event) => updateMoneyField("budget_max", event.target.value)}
                            className={inputClass}
                            placeholder="Max"
                          />
                        </div>
                        {(fieldErrors.budget_min || fieldErrors.budget_max) ? (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.budget_min || fieldErrors.budget_max}</p>
                        ) : null}
                      </label>

                      {detailBookingType === "corporate" ? (
                        <label className="text-xs font-semibold text-gray-700 md:text-right">
                          Number of Attendees
                          <input
                            type="number"
                            min="0"
                            value={formData.attendee_count}
                            disabled={fieldsDisabled}
                            onChange={(event) => setFormData((prev) => ({ ...prev, attendee_count: event.target.value }))}
                            className={`${inputClass} mt-1`}
                          />
                        </label>
                      ) : null}

                      {detailBookingType === "concert" ? (
                        <label className="text-xs font-semibold text-gray-700 md:text-right">
                          Event Name
                          <input
                            value={formData.event_name}
                            disabled={fieldsDisabled}
                            onChange={(event) => setFormData((prev) => ({ ...prev, event_name: event.target.value }))}
                            className={`${inputClass} mt-1`}
                          />
                        </label>
                      ) : (
                        <p className="text-xs font-semibold text-gray-700 md:text-right pt-1">
                          Target Date: <span className="font-normal">{formatDisplayDate(booking.event_date || booking.created_at)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {detailBookingType === "concert" ? (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="divide-y divide-gray-400 text-sm text-gray-800">
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-0 px-4 py-3 md:px-6">
                        <span className="font-semibold">Technical Rider :</span>
                        <input
                          value={formData.technical_rider}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, technical_rider: event.target.value }))}
                          className={`h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-xs focus:border-gray-500 focus:bg-white focus:outline-none ${disabledFieldClass}`}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-0 px-4 py-3 md:px-6">
                        <span className="font-semibold">Musical Director :</span>
                        <input
                          value={formData.musical_director}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, musical_director: event.target.value }))}
                          className={`h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-xs focus:border-gray-500 focus:bg-white focus:outline-none ${disabledFieldClass}`}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-0 px-4 py-3 md:px-6">
                        <span className="font-semibold">Food:</span>
                        <input
                          value={formData.food}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, food: event.target.value }))}
                          className={`h-8 w-full rounded border border-gray-300 bg-gray-50 px-2 text-xs focus:border-gray-500 focus:bg-white focus:outline-none ${disabledFieldClass}`}
                        />
                      </div>
                      <div className="px-4 py-4 md:px-6">
                        <p className="mb-2 font-semibold">Additional Requirements:</p>
                        <textarea
                          value={formData.details}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, details: event.target.value }))}
                          rows={5}
                          className={textAreaClass}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="divide-y divide-gray-400 text-sm text-gray-800">
                      <div className="px-4 py-4 md:px-6">
                        <p className="mb-2 font-semibold">Specific Requirements</p>
                        <textarea
                          value={formData.specific_needs}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, specific_needs: event.target.value }))}
                          rows={4}
                          className={textAreaClass}
                        />
                      </div>
                      <div className="px-4 py-4 md:px-6">
                        <p className="mb-2 font-semibold">Event Details & Other Requirements:</p>
                        <textarea
                          value={formData.details}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, details: event.target.value }))}
                          rows={5}
                          className={textAreaClass}
                        />
                      </div>
                    </div>
                  </div>
                )}
                </>
                ) : null}

                {editConsultation ? (
                  <div className="overflow-hidden rounded-xl border border-blue-300 bg-white shadow-sm">
                    <div className="bg-blue-700 px-4 py-2 text-sm font-bold text-white">Initial Consultation Schedule</div>
                    <div className="space-y-4 p-4 md:p-6">
                      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        <p className="font-semibold">{getConsultationStatusLabel(consultationStatus)}</p>
                        {booking?.consultation_scheduled_at ? (
                          <p className="mt-1 text-blue-800">
                            Current schedule: {formatConsultationDateTime(booking.consultation_scheduled_at)}
                          </p>
                        ) : (
                          <p className="mt-1 text-blue-800">The client has not submitted a consultation schedule yet.</p>
                        )}
                      </div>

                      <label className="block text-xs font-semibold text-gray-700">
                        Consultation date and time
                        <input
                          type="datetime-local"
                          value={formData.consultation_datetime || ""}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, consultation_datetime: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                      </label>

                      <label className="block text-xs font-semibold text-gray-700">
                        Meeting Place
                        <input
                          type="text"
                          value={formData.meeting_place || ""}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, meeting_place: event.target.value }))}
                          placeholder="e.g. EchoJham Office, Meeting Room 2"
                          className={`${inputClass} mt-1`}
                        />
                      </label>

                      <label className="block text-xs font-semibold text-gray-700">
                        Meeting Instructions
                        <textarea
                          value={formData.meeting_instructions || ""}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, meeting_instructions: event.target.value }))}
                          placeholder="e.g. Please look for Juan at the reception desk. Ask for the EchoJham Events Team. The meeting will be held on the 2nd floor."
                          rows={4}
                          className={`${textAreaClass} mt-1`}
                        />
                      </label>

                      <p className="text-xs text-gray-500">
                        Saving an updated date and time will mark the consultation as scheduled for the client. Meeting place and instructions are optional.
                      </p>
                    </div>
                  </div>
                ) : null}

                {showConsultationSummary ? (
                  <div className="overflow-hidden rounded-xl border border-blue-300 bg-white shadow-sm">
                    <div className="bg-blue-700 px-4 py-2 text-sm font-bold text-white">Initial Consultation Schedule</div>
                    <div className="grid gap-3 p-4 text-xs text-gray-700 md:p-6">
                      <p><span className="font-semibold">Status:</span> {getConsultationStatusLabel(consultationStatus)}</p>
                      <p><span className="font-semibold">Scheduled time:</span> {booking?.consultation_scheduled_at ? formatConsultationDateTime(booking.consultation_scheduled_at) : "—"}</p>
                      <p><span className="font-semibold">Meeting Place:</span> {summaryValue(formData.meeting_place || booking?.meeting_place)}</p>
                      <p><span className="font-semibold">Meeting Instructions:</span> {summaryValue(formData.meeting_instructions || booking?.meeting_instructions)}</p>
                    </div>
                  </div>
                ) : null}

                {editProposalContract ? (
                  <>
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="bg-black px-4 py-2 text-sm font-bold text-white">Contract Fields</div>
                    <div className="grid gap-4 p-4 md:grid-cols-2 md:p-6">
                      <label className="text-xs font-semibold text-gray-700">
                        EchoJham Representative
                        <input
                          value={formData.echojham_representative}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, echojham_representative: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                      </label>
                      <label className="text-xs font-semibold text-gray-700">
                        Approved Quotation / Total Fee
                        <input
                          type="text"
                          inputMode="numeric"
                          value={displayMoneyValue(formData.approved_quotation_total, { focused: focusedMoneyField === "approved_quotation_total" })}
                          disabled={fieldsDisabled}
                          onFocus={() => setFocusedMoneyField("approved_quotation_total")}
                          onBlur={() => setFocusedMoneyField("")}
                          onChange={(event) => updateMoneyField("approved_quotation_total", event.target.value)}
                          className={`${inputClass} mt-1`}
                        />
                        {fieldErrors.approved_quotation_total ? (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.approved_quotation_total}</p>
                        ) : null}
                      </label>
                      <label className="text-xs font-semibold text-gray-700">
                        Down Payment / Reservation Fee
                        <input
                          type="text"
                          inputMode="numeric"
                          value={displayMoneyValue(formData.down_payment_amount, { focused: focusedMoneyField === "down_payment_amount" })}
                          disabled={fieldsDisabled}
                          onFocus={() => setFocusedMoneyField("down_payment_amount")}
                          onBlur={() => setFocusedMoneyField("")}
                          onChange={(event) => updateMoneyField("down_payment_amount", event.target.value)}
                          className={`${inputClass} mt-1`}
                        />
                        {fieldErrors.down_payment_amount ? (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.down_payment_amount}</p>
                        ) : null}
                      </label>
                      <label className="text-xs font-semibold text-gray-700">
                        Balance Due Date
                        <input
                          type="date"
                          value={formData.balance_due_date}
                          disabled={fieldsDisabled}
                          min={getDateInputMinYmd({ originalYmd: booking?.balance_due_date }) || undefined}
                          onChange={(event) => {
                            setFieldErrors((prev) => ({ ...prev, balance_due_date: "" }));
                            setFormData((prev) => ({ ...prev, balance_due_date: event.target.value }));
                          }}
                          className={`${inputClass} mt-1`}
                        />
                        {fieldErrors.balance_due_date ? (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.balance_due_date}</p>
                        ) : null}
                      </label>
                      <label className="text-xs font-semibold text-gray-700 md:col-span-2">
                        Payment Method
                        <input
                          value={formData.payment_method}
                          disabled={fieldsDisabled}
                          onChange={(event) => setFormData((prev) => ({ ...prev, payment_method: event.target.value }))}
                          className={`${inputClass} mt-1`}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-purple-300 bg-white shadow-sm">
                    <div className="bg-purple-800 px-4 py-2 text-sm font-bold text-white">Proposal & Contract</div>
                    <div className="space-y-4 p-4 md:p-6 text-sm text-gray-800">
                      <p className="font-semibold">{getProposalReviewLabel(booking)}</p>
                      <div className="grid gap-2 text-xs text-gray-700">
                        <p>Client I Agree: {booking?.client_agreement_confirmed || booking?.agreement_confirmed ? "Recorded" : "Not yet recorded"}</p>
                        <p>Signed contract PDF: {hasSignedContract(booking) ? "Uploaded" : "Missing"}</p>
                        <p>Contract review: {getContractReviewLabel(booking)}</p>
                      </div>

                      {booking?.proposal_review_status === PROPOSAL_REVIEW_STATUSES.PENDING_ADMIN && pendingProposalChanges ? (
                        <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3 text-xs text-purple-950">
                          <p className="font-semibold mb-2">Client submitted these edits:</p>
                          {Object.entries(pickProposalEditValues(pendingProposalChanges)).map(([key, value]) => (
                            key === "concert_vendor_choices" ? (
                              <p key={key} className="mb-1"><span className="font-semibold">Vendors:</span> {JSON.stringify(value || {})}</p>
                            ) : (
                              <p key={key} className="mb-1"><span className="font-semibold">{key.replace(/_/g, " ")}:</span> {String(value || "—")}</p>
                            )
                          ))}
                        </div>
                      ) : null}

                      {formData.status === "proposal_contract" && !pacComplete ? (
                        <div className="flex flex-wrap justify-end gap-2">
                          {booking?.proposal_review_status === PROPOSAL_REVIEW_STATUSES.PENDING_ADMIN ? (
                            <button
                              type="button"
                              onClick={() => reviewProposalChanges("revise")}
                              disabled={statusUpdating}
                              className="inline-flex w-full sm:w-auto items-center justify-center rounded bg-white px-4 py-2 text-sm font-semibold text-gray-800 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Request Revision
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => reviewProposalChanges("approve")}
                            disabled={statusUpdating}
                            className="inline-flex w-full sm:w-auto items-center justify-center rounded bg-[#5b21b6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4c1d95] disabled:opacity-50"
                          >
                            Approve Details
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                {hasSignedContract(booking) ? (
                  <div className="overflow-hidden rounded-xl border border-gray-400 bg-white shadow-sm">
                    <div className="bg-black px-4 py-2 text-sm font-bold text-white">Signed Contract</div>
                    <div className="space-y-4 px-4 py-4 md:px-6 text-sm text-gray-800">
                      <p className="font-semibold">{getContractReviewLabel(booking)}</p>
                      {booking?.contract_review_status === CONTRACT_REVIEW_STATUSES.NEEDS_REVISION && booking?.contract_revision_message ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950">
                          <p className="font-semibold mb-1">Resubmission reason sent to client</p>
                          <p>{booking.contract_revision_message}</p>
                        </div>
                      ) : null}
                      <p className="text-xs text-gray-600">Client uploaded signed contract:</p>
                      {resolveContractUrl(signedContractAttachment) ? (
                        <div className="flex flex-wrap gap-3">
                          <a
                            href={resolveContractUrl(signedContractAttachment)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 underline break-all"
                          >
                            {signedContractAttachment.name || signedContractAttachment.path || "View signed contract"}
                          </a>
                          <a
                            href={resolveContractUrl(signedContractAttachment)}
                            target="_blank"
                            rel="noreferrer"
                            download={signedContractAttachment.name || "signed-contract.pdf"}
                            className="text-blue-700 underline"
                          >
                            Download
                          </a>
                        </div>
                      ) : (
                        <p className="break-all">{signedContractAttachment.name || signedContractAttachment.path || "Signed contract uploaded"}</p>
                      )}
                      {!isContractAccepted(booking) ? (
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setResubmitReason("");
                              setShowResubmitModal(true);
                            }}
                            disabled={statusUpdating || !canAdminAcceptContract(booking)}
                            className="inline-flex w-full sm:w-auto items-center justify-center rounded bg-white px-4 py-2 text-sm font-semibold text-gray-800 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Request Resubmission
                          </button>
                          <button
                            type="button"
                            onClick={acceptSignedContract}
                            disabled={statusUpdating || !canAdminAcceptContract(booking)}
                            className="inline-flex w-full sm:w-auto items-center justify-center rounded bg-[#0f7b2e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d6827] disabled:opacity-50"
                          >
                            Accept Contract
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {pacComplete ? (
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={acceptBookingAfterContract}
                      disabled={statusUpdating}
                      className="inline-flex w-full sm:w-auto items-center justify-center rounded bg-[#0f7b2e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d6827] disabled:opacity-50"
                    >
                      Confirm & Move to Event Preparation
                    </button>
                  </div>
                ) : null}
                  </>
                ) : null}

                {showProposalContractSummary ? (
                  <div className="overflow-hidden rounded-xl border border-purple-300 bg-white shadow-sm">
                    <div className="bg-purple-800 px-4 py-2 text-sm font-bold text-white">Proposal & Contract</div>
                    <div className="grid gap-3 p-4 text-xs text-gray-700 md:grid-cols-2 md:p-6">
                      <p><span className="font-semibold">EchoJham Representative:</span> {summaryValue(formData.echojham_representative)}</p>
                      <p><span className="font-semibold">Approved Quotation / Total Fee:</span> {summaryValue(displayMoneyValue(formData.approved_quotation_total))}</p>
                      <p><span className="font-semibold">Down Payment / Reservation Fee:</span> {summaryValue(displayMoneyValue(formData.down_payment_amount))}</p>
                      <p><span className="font-semibold">Balance Due Date:</span> {formData.balance_due_date ? formatDisplayDate(formData.balance_due_date) : "—"}</p>
                      <p><span className="font-semibold">Payment Method:</span> {summaryValue(formData.payment_method)}</p>
                      <p className="md:col-span-2"><span className="font-semibold">Proposal:</span> {getProposalReviewLabel(booking)}</p>
                      <p><span className="font-semibold">Client I Agree:</span> {booking?.client_agreement_confirmed || booking?.agreement_confirmed ? "Recorded" : "Not yet recorded"}</p>
                      <p><span className="font-semibold">Contract review:</span> {getContractReviewLabel(booking)}</p>
                      <p className="md:col-span-2">
                        <span className="font-semibold">Signed contract:</span>{" "}
                        {resolveContractUrl(signedContractAttachment) ? (
                          <a
                            href={resolveContractUrl(signedContractAttachment)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 underline break-all"
                          >
                            {signedContractAttachment.name || signedContractAttachment.path || "View signed contract"}
                          </a>
                        ) : (
                          hasSignedContract(booking) ? (signedContractAttachment?.name || signedContractAttachment?.path || "Uploaded") : "Missing"
                        )}
                      </p>
                    </div>
                  </div>
                ) : null}

                {isDeclined ? null : (
                <div className="flex flex-wrap justify-end gap-4 pt-3">
                  {isLocked ? (
                    <button
                      type="button"
                      disabled={statusUpdating || showConfirmModal}
                      onClick={showConfirmation}
                      className={`${sharedButtonClass} bg-[#2d6d2d] text-white hover:bg-[#245c24]`}
                    >
                      <span>{statusUpdating ? "Saving..." : "Submit"}</span>
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={statusUpdating}
                        onClick={cancelEditing}
                        className={`${sharedButtonClass} border border-gray-500 bg-white text-gray-800 hover:bg-gray-100`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={statusUpdating || showConfirmModal}
                        onClick={showConfirmation}
                        className={`${sharedButtonClass} bg-[#2d6d2d] text-white hover:bg-[#245c24]`}
                      >
                        <span>{statusUpdating ? "Saving..." : "Save Changes"}</span>
                      </button>
                    </>
                  )}
                </div>
                )}

                {showConfirmModal && confirmModalData && (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-lg border border-gray-300 bg-white p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Changes</h3>
                      <p className="text-sm text-gray-700 mb-4">
                        Are you sure you want to save these changes to the booking?
                      </p>
                      
                      <div className="mb-4 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-3 max-h-40 overflow-y-auto">
                        {confirmModalData.uiStatus && confirmModalData.uiStatus !== (booking.status || deriveUiStatus(booking)) && (
                          <div className="mb-2">
                            <span className="font-semibold">Status:</span> {statusLabel(booking.status || deriveUiStatus(booking))} → {statusLabel(confirmModalData.uiStatus)}
                          </div>
                        )}
                        {confirmModalData.event_name !== booking.event_name && (
                          <div className="mb-2">
                            <span className="font-semibold">Event Name:</span> {confirmModalData.event_name || "(empty)"}
                          </div>
                        )}
                        {confirmModalData.budget_range !== booking.budget_range && (
                          <div className="mb-2">
                            <span className="font-semibold">Budget:</span> {confirmModalData.budget_range || "(empty)"}
                          </div>
                        )}
                        {confirmModalData.theme !== booking.theme && (
                          <div className="mb-2">
                            <span className="font-semibold">Theme:</span> {confirmModalData.theme || "(empty)"}
                          </div>
                        )}
                        {confirmModalData.venue !== booking.venue && (
                          <div className="mb-2">
                            <span className="font-semibold">Venue:</span> {confirmModalData.venue || "(empty)"}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowConfirmModal(false);
                            setConfirmModalData(null);
                          }}
                          className="flex-1 w-full sm:w-auto px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => updateBookingDetails()}
                          disabled={statusUpdating}
                          className="flex-1 w-full sm:w-auto px-4 py-2 rounded bg-[#2d6d2d] text-white hover:bg-[#245c24] font-semibold text-sm disabled:opacity-50"
                        >
                          {statusUpdating ? "Saving..." : "Confirm & Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showResubmitModal ? (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-lg border border-gray-300 bg-white p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Request Resubmission</h3>
                      <p className="text-sm text-gray-700 mb-4 break-words">
                        Tell the client why the signed contract needs to be replaced. This reason is required.
                      </p>
                      <textarea
                        value={resubmitReason}
                        onChange={(event) => setResubmitReason(event.target.value)}
                        rows={4}
                        className={textAreaClass}
                        placeholder="Required revision reason"
                      />
                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowResubmitModal(false);
                            setResubmitReason("");
                          }}
                          className="flex-1 w-full sm:w-auto px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={requestContractResubmission}
                          disabled={statusUpdating || !String(resubmitReason || "").trim()}
                          className="flex-1 w-full sm:w-auto px-4 py-2 rounded bg-[#8b0000] text-white hover:bg-[#6f0000] font-semibold text-sm disabled:opacity-50"
                        >
                          {statusUpdating ? "Sending..." : "Send to Client"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {showDeclineModal ? (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-lg border border-gray-300 bg-white p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Decline Booking</h3>
                      <p className="text-sm text-gray-700 mb-4 break-words">
                        Choose why this booking is being declined. This reason is required, shown to the client, and cannot be undone.
                      </p>
                      <div className="space-y-2 mb-3">
                        {DECLINE_REASON_OPTIONS.map((option) => (
                          <label key={option.value} className="flex items-start gap-2 text-sm text-gray-800">
                            <input
                              type="radio"
                              name="decline-reason"
                              value={option.value}
                              checked={declineReasonChoice === option.value}
                              onChange={(event) => setDeclineReasonChoice(event.target.value)}
                              className="mt-0.5"
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                      {declineReasonChoice === DECLINE_REASON_OTHER ? (
                        <textarea
                          value={declineReason}
                          onChange={(event) => setDeclineReason(event.target.value)}
                          rows={4}
                          className={textAreaClass}
                          placeholder="Required custom decline reason"
                        />
                      ) : null}
                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          type="button"
                          onClick={cancelDecline}
                          className="flex-1 w-full sm:w-auto px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={confirmDecline}
                          disabled={statusUpdating || !selectedDeclineReason}
                          className="flex-1 w-full sm:w-auto px-4 py-2 rounded bg-[#8b0000] text-white hover:bg-[#6f0000] font-semibold text-sm disabled:opacity-50"
                        >
                          {statusUpdating ? "Declining..." : "Decline Booking"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
